import { useState, type FormEvent } from 'react'
import { supabase } from '@/lib/supabase'
import { insertEvent } from '@/lib/events'
import { pt } from '@/i18n/pt-PT'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { FormField } from '@/components/ui/FormField'

type MfaChallengeProps = {
  factorId: string
  onVerified: () => void
}

export function MfaChallenge({ factorId, onVerified }: MfaChallengeProps) {
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    const { data: challenge, error: chErr } = await supabase.auth.mfa.challenge({ factorId })
    if (chErr || !challenge) {
      setSubmitting(false)
      setError(pt.auth.mfaInvalidCode)
      return
    }
    const { error: verifyErr } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challenge.id,
      code,
    })
    setSubmitting(false)
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (verifyErr) {
      if (user) {
        await insertEvent({
          type: 'user.mfa_challenge',
          subjectType: 'user',
          subjectId: user.id,
          payload: { outcome: 'failed' },
        })
      }
      setError(pt.auth.mfaInvalidCode)
      return
    }
    if (user) {
      await insertEvent({
        type: 'user.mfa_challenge',
        subjectType: 'user',
        subjectId: user.id,
        payload: { outcome: 'verified' },
      })
    }
    onVerified()
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas p-8">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm space-y-5 rounded-lg border border-DEFAULT bg-surface p-8 shadow-card"
      >
        <div>
          <h1 className="text-[18px] font-semibold text-gray-900">
            {pt.auth.mfaChallengeTitle}
          </h1>
          <p className="mt-1 text-[12px] text-gray-500">{pt.auth.mfaChallengeIntro}</p>
        </div>
        <FormField id="mfa-code" label={pt.auth.mfaCodeLabel} required>
          <Input
            id="mfa-code"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            autoComplete="one-time-code"
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            required
            className="text-center font-mono text-[16px] tracking-[0.4em]"
          />
        </FormField>
        {error && (
          <p className="text-[11.5px] text-red-700" role="alert">
            {error}
          </p>
        )}
        <Button
          type="submit"
          disabled={submitting || code.length !== 6}
          className="h-9 w-full text-[13px]"
        >
          {pt.auth.mfaVerify}
        </Button>
      </form>
    </main>
  )
}
